const all = new Map<string, GeckoView>()

export enum GeckoViewCategory {
  THICKNESS = 'THICKNESS',
  LOSS = 'LOSS',
  DERIVED = 'DERIVED',
  MORE = 'MORE',
}

/** Base properties shared by both colorcet config types */
interface ColorcetConfigBase {
  /** The start of the Colorcet spectrum, between 0 and 1) */
  start: number
  /** The middle of the Colorcet spectrum, between start and end */
  middle: number
  /** The end of the Colorcet spectrum, between 0 and 1 */
  end: number
}

/** Colorcet config using predefined colorcet ID */
interface StandardColorcetConfig extends ColorcetConfigBase {
  /** The Colorcet colorcetId, i.e. "R2" */
  colorcetId: string
  customColorScale?: never
}

/** Colorcet config using custom color array */
interface CustomColorcetConfig extends ColorcetConfigBase {
  colorcetId?: never
  /** Custom color scale data as an array of color strings */
  customColorScale: ReadonlyArray<string>
}

/** Union type ensuring colorcetId and customColorScale are mutually exclusive */
export type ColorcetConfig = StandardColorcetConfig | CustomColorcetConfig

const StoplightColorcetConfig: ColorcetConfig = {
  colorcetId: 'R2',
  start: 1.0,
  middle: 0.6,
  end: 0.3,
}

const inverseStopLightColorcetConfig: ColorcetConfig = {
  colorcetId: 'R2',
  start: 0.3,
  middle: 0.6,
  end: 1.0,
}

// Custom color configuration for Thickness Condition categorization
const ThicknessConditionColorcetConfig: ColorcetConfig = {
  colorcetId: 'CBL1', // A different color scheme - you can try: 'L04', 'D01', 'R2', 'CBL1', etc.
  start: 0.0, // Maps to the lowest category values (red/critical)
  middle: 0.5, // Maps to the middle category values (yellow/warning)
  end: 1.0, // Maps to the highest category values (green/good)
}

// Special discrete color configuration for EMAT values 0-6
// This will be used by the color scale system to create discrete color mappings
// Array indices correspond to EMAT category values (0-6)
export const EmatDiscreteColors = [
  'F4A460', // 0: saddle brown - no wastage data available
  'FF6600', // 1: orange - condition not verifiable
  '2D7D32', // 2: muted green - no discernable metal loss (best condition)
  '689F38', // 3: muted lime green - minimal wastage (0-5%)
  'FFC800', // 4: yellow - moderate wastage (5-20%)
  'FF6600', // 5: orange - significant wastage (20-25%)
  'FF3000', // 6: standard red - severe wastage (>25%) (worst condition)
] as const

const EmatDiscreteColorcetConfig: ColorcetConfig = {
  start: 0.0,
  middle: 0.5,
  end: 1.0,
  customColorScale: EmatDiscreteColors,
}

/**
 * An object representing a data "view" (a type of data... all data of the same
 * view can be fairly compared and contrasted). Data views can be accessed
 * via their singleton instance directly or by ID lookup.
 */
export class GeckoView {
  //----------------------------------------------------------------------------

  public static readonly Description = 'The type of data to display'

  //----------------------------------------------------------------------------

  public static readonly Thickness = new GeckoView(
    'thickness',
    'Thickness',
    'Surface thickness',
    GeckoViewCategory.THICKNESS,
    StoplightColorcetConfig
  )

  public static readonly PercentBelowTmin = new GeckoView(
    'percent-below-tmin',
    'Percent Below Tmin',
    'Percent of Points in a bin that are below Tmin',
    GeckoViewCategory.MORE,
    inverseStopLightColorcetConfig
  )

  public static readonly PercentAscansBelowTmin = new GeckoView(
    'percent-ascans-below-tmin',
    'Percent Ascans Below Tmin',
    'Percent of Ascans that are below Tmin',
    GeckoViewCategory.MORE,
    inverseStopLightColorcetConfig
  )

  public static readonly Deformation = new GeckoView(
    'deformation',
    'Deformation',
    'Predicted plate sagging',
    GeckoViewCategory.MORE,
    { colorcetId: 'D01', start: 0, middle: 0.5, end: 1 }
  )
  public static readonly TopographicMap = new GeckoView(
    'topographic-map',
    'Topographic Map',
    'Elevation measurements of surface topography',
    GeckoViewCategory.MORE,
    { colorcetId: 'L08', start: 0, middle: 0.5, end: 1 }
  )
  public static readonly AScansPerBin = new GeckoView(
    'a-scans-per-bin',
    'Measurements per Bin',
    'Number of measurements taken in a given bin',
    GeckoViewCategory.MORE,
    { colorcetId: 'D03', start: 0.5, middle: 0.25, end: 0.0 },
    true
  )
  public static readonly BinStandardDeviation = new GeckoView(
    'bin-standard-deviation',
    'Standard Deviation',
    'Standard deviation of measurements in a given bin',
    GeckoViewCategory.MORE,
    { colorcetId: 'L17', start: 0, middle: 0.5, end: 1 },
    true
  )
  public static readonly Amplitude = new GeckoView(
    'amplitude',
    'Amplitude',
    'Amplitude measurements',
    GeckoViewCategory.MORE,
    { colorcetId: 'L01', start: 0.2, middle: 0.85, end: 1.0 }
  )

  public static readonly OutOfRoundness = new GeckoView(
    'out-of-roundness',
    'Out-of-Roundness',
    'Deviation from perfect roundness - near-zero values are ideal, extremes indicate problems',
    GeckoViewCategory.MORE,
    { colorcetId: 'D01', start: 0, middle: 0.5, end: 1 }
  )

  //----------------------------------------------------------------------------

  public static readonly DifferenceFromNominal = new GeckoView(
    'difference-from-nominal',
    'Difference from Nominal',
    'Difference between thickness and nominal',
    GeckoViewCategory.LOSS,
    StoplightColorcetConfig
  )

  public static readonly DifferenceFromNominalProjection = new GeckoView(
    'difference-from-nominal-projection',
    'Difference from Nominal (Projection)',
    'Difference between thickness and nominal',
    GeckoViewCategory.LOSS,
    inverseStopLightColorcetConfig
  )

  public static readonly RemainingCorrosionAllowance = new GeckoView(
    'remaining-corrosion-allowance',
    'Remaining Corrosion Allowance',
    'Difference between thickness and t-min',
    GeckoViewCategory.LOSS,
    StoplightColorcetConfig
  )

  public static readonly RemainingCorrosionAllowancePercent = new GeckoView(
    'remaining-corrosion-allowance-percent',
    'Remaining Corrosion Allowance Percent',
    'Difference between thickness and t-min as a percentage of Tmin',
    GeckoViewCategory.LOSS,
    StoplightColorcetConfig
  )

  public static readonly LongTermCorrosionRate = new GeckoView(
    'long-term-corrosion-rate',
    'Long-Term Corrosion Rate',
    'Rate of corrosion of an asset over time',
    GeckoViewCategory.LOSS,
    inverseStopLightColorcetConfig
  )

  public static readonly LongTermCorrosionRateWithHistoricalCoating =
    new GeckoView(
      'long-term-corrosion-rate-with-historical-coating',
      'Long-Term Corrosion Rate with Historical Coating',
      'Rate of corrosion of an asset over time accounting for historical coating',
      GeckoViewCategory.LOSS,
      inverseStopLightColorcetConfig
    )

  public static readonly RemainingLife = new GeckoView(
    'remaining-life',
    'Remaining Life',
    'Remaining Life is calculated using a Long-Term Corrosion Rate based on nominal and the latest inspection.',
    GeckoViewCategory.DERIVED,
    StoplightColorcetConfig
  )

  public static readonly CustomCRRemainingLife = new GeckoView(
    // TODO: Make this a separate param on the CDF
    // TODO: Make the label and description programmatic based on user input
    'remaining-life-custom-cr',
    'Remaining Life per API Report',
    'Remaining Life is calculated using corrosion rates per API report',
    GeckoViewCategory.DERIVED,
    StoplightColorcetConfig
  )

  public static readonly RemainingLifeWithHistoricalCoating = new GeckoView(
    // TODO: Make this a separate param on the CDF
    // TODO: Make the label and description programmatic based on user input
    'remaining-life-with-historical-coating',
    'Remaining Life with Historical Coating',
    'Remaining Life is calculated use a Long-Term Corrosion Rate based on nominal and the latest inspection, accounting for historical coating',
    GeckoViewCategory.DERIVED,
    StoplightColorcetConfig
  )

  public static readonly TimeSinceLastRepair = new GeckoView(
    'time-since-last-repair',
    'Time Since Last Repair',
    'The time between the last repair and the current inspection',
    GeckoViewCategory.DERIVED,
    { colorcetId: 'L01', start: 0.2, middle: 0.85, end: 1.0 }
  )

  public static readonly Velocity = new GeckoView(
    'velocity',
    'Velocity',
    'Velocity readings from a concrete inspection at a specific depth',
    GeckoViewCategory.MORE,
    { colorcetId: 'CBL1', start: 1.0, middle: 0.3, end: 0.0 }
  )
  public static readonly DefectType = new GeckoView(
    'defectType',
    'Defect Type',
    'Defect type indications from a concrete inspection across all concrete data layers',
    GeckoViewCategory.MORE,
    { colorcetId: 'CBL1', start: 1.0, middle: 0.3, end: 0.0 }
  )
  public static readonly EMATCategory = new GeckoView(
    'emat-category',
    'Validation',
    'Validation of EMAT data',
    GeckoViewCategory.MORE,
    StoplightColorcetConfig
  )

  public static readonly VelocityZScore = new GeckoView(
    'velocity-zscore',
    'Velocity Z-Score',
    'The z-scores of the velocity readings from SASW. This is a compilation of all depths',
    GeckoViewCategory.MORE,
    { colorcetId: 'L04', start: 0, middle: 0.5, end: 1 }
  )

  public static readonly ThicknessCondition = new GeckoView(
    'thickness-condition',
    'Thickness Condition',
    'Categorical assessment of thickness condition based on nominal ratio',
    GeckoViewCategory.MORE,
    ThicknessConditionColorcetConfig
  )

  public static readonly EmatCustomThicknessCondition = new GeckoView(
    'emat-custom-thickness-condition',
    'Categorical',
    'EMAT categorization based on validity and wastage',
    GeckoViewCategory.MORE,
    EmatDiscreteColorcetConfig
  )

  //----------------------------------------------------------------------------

  /** A unique ID which should line up with Data Systems and even Portal */
  public readonly id: string

  /** A user-facing colorcetId for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this a specific data view */
  public readonly description: string

  /** An enum used to group views in the left sidebar */
  public readonly category: GeckoViewCategory

  /** Information used to determine color scales for this view */
  public readonly colorcet: ColorcetConfig

  /** A boolean to determine if the GeckoView is only visible to Gecko users */
  public readonly geckoOnly: boolean

  private constructor(
    id: string,
    name: string,
    description: string,
    category: GeckoViewCategory,
    colorcet: ColorcetConfig,
    geckoOnly?: boolean
  ) {
    this.id = id
    this.name = name
    this.description = description
    this.category = category
    this.colorcet = colorcet
    this.geckoOnly = geckoOnly ?? false
    all.set(id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * A default sorting method for component data frame views
   * @param a The first view object
   * @param b The second view object
   */
  public static DefaultSort(a: GeckoView, b: GeckoView): number {
    return defaultOrder.indexOf(a.id) - defaultOrder.indexOf(b.id)
  }

  /**
   * Returns the unique data view instance for a given ID if it exists
   * @param id The unique ID of this data view
   */
  public static GetByID(id: string): GeckoView | undefined {
    return all.get(id)
  }

  /**
   * Returns the unique data view instance for a given ID, or creates a
   * unique data view instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string): GeckoView {
    return (
      all.get(id) ??
      new GeckoView(id, id, '', GeckoViewCategory.MORE, {
        start: 0,
        middle: 0.5,
        end: 1,
        colorcetId: 'L12',
      })
    )
  }
}

const defaultOrder = [
  GeckoView.DifferenceFromNominal.id,
  GeckoView.DifferenceFromNominalProjection.id,
  GeckoView.Thickness.id,
  GeckoView.OutOfRoundness.id,
  GeckoView.Deformation.id,
  GeckoView.AScansPerBin.id,
  GeckoView.BinStandardDeviation.id,
  GeckoView.RemainingCorrosionAllowance.id,
  GeckoView.RemainingCorrosionAllowancePercent.id,
  GeckoView.LongTermCorrosionRate.id,
  GeckoView.LongTermCorrosionRateWithHistoricalCoating.id,
  GeckoView.RemainingLife.id,
  GeckoView.CustomCRRemainingLife.id,
  GeckoView.ThicknessCondition.id,
  GeckoView.TimeSinceLastRepair.id,
  GeckoView.PercentAscansBelowTmin.id,
  GeckoView.PercentBelowTmin.id,
]
